import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CurrencyEnum, DateFormats, IntervalFiltersType, SortingValue } from '@constants';
import {
  FiltersEntity,
  FiltersState,
  LegacyRequestSearchBody,
  MoneyFilterType,
  RequestSearchBody,
  useAppDispatch,
  useAppSelector,
  useProfileAPI,
} from '@innowise-group/core';

import {
  candidateLifecycleStatusSerializer,
  candidateResponsibleEmployeeDataSerializer,
  candidateStringDataSerializer,
  claimsToApproveStatusSerializer,
  formatFilterDate,
  lifecycleStatusSerializer,
  serializeBooleanList,
  serializeCandidateWorkFormatsData,
  serializeDateSegmentRange,
  serializeNestedSelectData,
  serializeOverdueData,
  serializerAgeRangeData,
  serializerBooleanData,
  serializerDateRangeData,
  serializeRequestAuthorEmploye,
  serializeRequestCustomerIds,
  serializeRequestExecutorIds,
  serializeRequestWorkLoads,
  serializerMoneyData,
  serializerNumberRangeData,
  serializerPlaceDataFilter,
  serializeSearchValue,
  serializeSelectData,
  serializeSelectNumberData,
  serializeSingleSelectOptionUnit,
} from './filters.mappers';
import { preloadedState } from './filters.preloaded-state';
import { getFiltersStateSelector } from './filters.selectors';
import {
  getAllCandidatesIntervalFiltersThunk,
  getCandidatesStatusesSelectOptionsThunk,
  getInitialCandidatesFilterThunk,
  getRequestFilterValuesThunk,
} from './filters.thunks';
import { RequestListFilter, RequestListFilters } from './filters.types';
import {
  addAndOpenFilterAction,
  addFilterAction,
  clearFiltersAction,
  closeFilterAction,
  FilterChangeArgs,
  onFilterChangeAction,
  removeFilterAction,
  resetSelectedAction,
  setCandidateFiltersOptionsAction,
  setCurrentTabAction,
  setPagableAction,
  setPageSortAction,
  setPreofferFiltersOptionsAction,
  setRequestFiltersOptionsAction,
  setSelectedAction,
  setVacancyFiltersOptionsAction,
  setCandidateFiltersByRequestAction,
  toggleFilterAction,
} from './fiters.actions';

export const useFiltersData = () => {
  return useAppSelector(getFiltersStateSelector);
};

export const usePagable = () => {
  const { pagable } = useFiltersData();
  const { setPagable } = useFilterMethods();
  return { ...pagable, setPagable };
};

export const useFilterMethods = (entity: keyof FiltersEntity = 'candidate') => {
  const dispatch = useAppDispatch();

  const resetFiltersState = useCallback(
    (blackListFields?: Parameters<typeof clearFiltersAction>[0]) => dispatch(clearFiltersAction(blackListFields)),
    [dispatch],
  );

  const getCandidatesStatusesSelectOptions = useCallback(() => {
    return dispatch(getCandidatesStatusesSelectOptionsThunk());
  }, [dispatch]);

  const getInitialCandidatesFilter = useCallback(
    (...args: Parameters<typeof getInitialCandidatesFilterThunk>) => {
      return dispatch(getInitialCandidatesFilterThunk(...args));
    },
    [dispatch],
  );

  const getAllCandidatesIntervalFilters = useCallback(
    (...args: Parameters<typeof getAllCandidatesIntervalFiltersThunk>) => {
      return dispatch(getAllCandidatesIntervalFiltersThunk(...args));
    },
    [dispatch],
  );

  const setCandidateFiltersOptions = useCallback(
    (args: Parameters<typeof setCandidateFiltersOptionsAction>[0]) => {
      dispatch(setCandidateFiltersOptionsAction(args));
    },
    [dispatch],
  );

  const setVacancyFiltersOptions = useCallback(
    (args: Parameters<typeof setVacancyFiltersOptionsAction>[0]) => {
      dispatch(setVacancyFiltersOptionsAction(args));
    },
    [dispatch],
  );

  const setRequestFiltersOptions = useCallback(
    (args: Parameters<typeof setRequestFiltersOptionsAction>[0]) => {
      dispatch(setRequestFiltersOptionsAction(args));
    },
    [dispatch],
  );

  const setPreofferFiltersOptions = useCallback(
    (args: Parameters<typeof setPreofferFiltersOptionsAction>[0]) => {
      dispatch(setPreofferFiltersOptionsAction(args));
    },
    [dispatch],
  );

  const setCandidateFiltersByRequest = useCallback(
    (request: Parameters<typeof setCandidateFiltersByRequestAction>[0]) => {
      dispatch(setCandidateFiltersByRequestAction(request));
    },
    [dispatch],
  );

  const addAndOpenFilter = useCallback(
    (filter: Pick<Parameters<typeof addAndOpenFilterAction>[0], 'filter'>['filter']) =>
      dispatch(addAndOpenFilterAction({ filter, entity })),
    [dispatch],
  );

  const addFilter = useCallback(
    (filter: Pick<Parameters<typeof addFilterAction>[0], 'filter'>['filter']) =>
      dispatch(addFilterAction({ filter, entity })),
    [dispatch],
  );

  const closeFilter = useCallback(
    (filter: Pick<Parameters<typeof closeFilterAction>[0], 'filter'>['filter']) =>
      dispatch(closeFilterAction({ filter, entity })),
    [dispatch],
  );

  const removeFilter = useCallback(
    (args: Omit<Parameters<typeof removeFilterAction>[0], 'entity'>) =>
      dispatch(removeFilterAction({ ...args, entity })),
    [dispatch],
  );

  const toggleFilter = useCallback(
    (filter: Pick<Parameters<typeof toggleFilterAction>[0], 'filter'>['filter']) =>
      dispatch(toggleFilterAction({ filter, entity })),
    [dispatch],
  );

  const onFilterChange = useCallback(
    (args: FilterChangeArgs) => dispatch(onFilterChangeAction({ entity, ...args })),
    [dispatch],
  );

  const setPagable = useCallback(
    (args: Parameters<typeof setPagableAction>[0]) => dispatch(setPagableAction(args)),
    [dispatch],
  );

  const setSelected = useCallback(
    (args: Parameters<typeof setSelectedAction>[0]) => dispatch(setSelectedAction(args)),
    [dispatch],
  );

  const resetSelected = useCallback(() => dispatch(resetSelectedAction()), [dispatch]);

  const setPageTab = useCallback(
    (args: Parameters<typeof setCurrentTabAction>[0]) => dispatch(setCurrentTabAction(args)),
    [dispatch],
  );

  const setPageSort = useCallback(
    (value: Parameters<typeof setPageSortAction>[0]['value']) => dispatch(setPageSortAction({ entity, value })),
    [dispatch],
  );

  return {
    addFilter,
    addAndOpenFilter,
    closeFilter,
    removeFilter,
    toggleFilter,
    onFilterChange,
    setPageSort,
    setPagable,
    setSelected,
    resetSelected,
    setPageTab,
    resetFiltersState,

    setCandidateFiltersOptions,
    setVacancyFiltersOptions,
    setRequestFiltersOptions,
    setPreofferFiltersOptions,
    setCandidateFiltersByRequest,
    getInitialCandidatesFilter,
    getAllCandidatesIntervalFilters,
    getCandidatesStatusesSelectOptions,
  };
};

export const useCandidateFilters = () => {
  const { candidate, responsible, currentTab, pagable, candidateOptions, sort, ...rest } = useFiltersData();
  const { ...filterMethods } = useFilterMethods('candidate');
  const { profile } = useProfileAPI();

  const reducedCandidateFilters = useMemo(() => {
    const currentRole = profile?.role?.name;

    return Object.keys(candidate).reduce((acc: FiltersState['candidate'], filterKey) => {
      if (
        currentRole &&
        Array.isArray(candidate[filterKey]?.availableForRoles) &&
        candidate[filterKey].availableForRoles.length > 0
      ) {
        if (!candidate[filterKey].availableForRoles.includes(currentRole)) {
          return acc;
        }
      }

      acc[filterKey] = candidate[filterKey];

      return acc;
    }, {} as FiltersState['candidate']) as FiltersState['candidate'];
  }, [candidate, profile?.role?.name]);

  const serialieFilters = (entity: FiltersState['candidate']) => ({
    ...serializeNestedSelectData('sourceFilter', 'source', 'sourceClarification', entity, 'sourceDetails'),
    ...serializerMoneyData('salary', 'salaryMin', 'salaryMax', entity),
    ...serializeSelectData('citizenship', entity),
    ...serializeSelectData('countries', entity),
    ...serializeSelectData('cities', entity),
    ...serializeSelectData('relocationCountries', entity),
    ...serializeSelectData('visaTypes', entity),
    ...serializeSelectData('statuses', entity),
    ...serializeSelectData('technologies', entity),
    ...serializeSelectData('domains', entity),
    ...serializerBooleanData('isBlocked', entity),
    ...serializerBooleanData('visaAvailability', entity),
    ...serializerBooleanData('relocationAvailability', entity),
    ...serializerBooleanData('readyForBusinessTrip', entity),
    ...serializerAgeRangeData('ageMin', 'ageMax', 'age', entity),
    ...serializerDateRangeData('birthDateMin', 'birthDateMax', 'birthDate', entity),
    ...serializerDateRangeData('statusDateMin', 'statusDateMax', 'candidateStatusDate', entity),
    ...serializerPlaceDataFilter('experienceFilter', entity),
    ...serializerPlaceDataFilter('courseFilter', entity),
    ...serializerPlaceDataFilter('educationFilter', entity),
    ...serializeNestedSelectData('statuses', 'status', 'substatuses', entity),
    ...serializeNestedSelectData('actualStatuses', 'status', 'substatuses', entity),
    ...serializeNestedSelectData('professionFilter', 'profession', 'grades', entity),
    ...serializeNestedSelectData('languageFilter', 'language', 'grades', entity),
    ...serializerNumberRangeData('overallJobExperienceMin', 'overallJobExperienceMax', 'overallJobExperience', entity),
    ...serializerNumberRangeData(
      'itSphereJobExperienceMin',
      'itSphereJobExperienceMax',
      'itSphereJobExperience',
      entity,
    ),
    ...serializeSingleSelectOptionUnit('gender', entity),
    ...candidateStringDataSerializer('name', 'searchBarRequest', entity),
    ...candidateLifecycleStatusSerializer('lifecycleStatus', currentTab),
    ...serializeCandidateWorkFormatsData('workFormats', entity, candidateOptions),
    ...candidateResponsibleEmployeeDataSerializer('responsible', responsible, currentTab),
    ...serializeRequestAuthorEmploye('employe', 'responsible', entity),
    ...serializeBooleanList('billable', entity),
    ...serializeRequestAuthorEmploye('vacancyIds', 'vacancyIds', entity),
    ...serializeSelectData('internationalCertificates', entity),
    ...(sort.candidate === SortingValue.Checked && { selectedCandidates: rest.selected }),
  });

  const getSerializedCandidateFilters = () => serialieFilters(reducedCandidateFilters);
  const getInitialCandidateFilters = () => serialieFilters(preloadedState.candidate);

  const serializedCandidateFilters = useMemo(getSerializedCandidateFilters, [
    responsible,
    currentTab,
    candidate.name.state.value,
    pagable,
    sort,
  ]);

  return {
    getSerializedCandidateFilters,
    getInitialCandidateFilters,
    filtersConfig: reducedCandidateFilters,
    serializedCandidateFilters,
    responsible,
    currentTab,
    pagable,
    sort,
    ...filterMethods,
    ...rest,
  };
};

// `/filter/all` returns salary bounds only in the default currency: another currency is loaded once it is selected.
export const useSalaryCurrencyBounds = ({ currency, variants }: MoneyFilterType['state']) => {
  const { getInitialCandidatesFilter } = useFilterMethods();
  const [loadingCurrencies, setLoadingCurrencies] = useState<CurrencyEnum[]>([]);

  const loadCurrencyBounds = useCallback(
    (nextCurrency: CurrencyEnum) => {
      if (variants?.[nextCurrency]?.initialDataLoaded || loadingCurrencies.includes(nextCurrency)) return;

      setLoadingCurrencies((current) => [...current, nextCurrency]);
      getInitialCandidatesFilter({ intervalType: IntervalFiltersType.Salary, currency: nextCurrency }).finally(() =>
        setLoadingCurrencies((current) => current.filter((item) => item !== nextCurrency)),
      );
    },
    [getInitialCandidatesFilter, loadingCurrencies, variants],
  );

  return { loadCurrencyBounds, isCurrencyBoundsLoading: loadingCurrencies.includes(currency) };
};

export const useRequestsFilters = () => {
  const { ...filterMethods } = useFilterMethods('request');
  const { request, responsible, currentTab, pagable, sort, ...rest } = useFiltersData();

  // A session restored after the request 2.0 release may still keep the removed filters
  const filtersConfig = useMemo(
    () =>
      Object.keys(preloadedState.request).reduce((accum, key) => {
        accum[key] = request[key] || preloadedState.request[key];
        return accum;
      }, {} as FiltersState['request']),
    [request],
  );

  const serialieFilters = (entity: FiltersState['request']): RequestSearchBody & LegacyRequestSearchBody => ({
    ...claimsToApproveStatusSerializer('claimsToApprove', currentTab),
    ...serializeRequestAuthorEmploye('vacancyIds', 'vacancyIds', entity),
    ...serializeSingleSelectOptionUnit('hiringPurpose', entity),
    ...serializeRequestWorkLoads('workload', entity),
    ...serializeSelectData('workFormats', entity),
    ...serializeSelectData('employmentForms', entity),
    ...serializeSelectData('statuses', entity),
    ...serializeOverdueData('isOverdue', entity),

    ...lifecycleStatusSerializer('lifecycleStatus', currentTab),
    ...serializeSearchValue('searchBarRequest', 'searchBarRequest', entity),
    ...serializeSelectData('priorities', entity),
    ...serializeBooleanList('isBillable', entity),
    ...serializeSelectNumberData('unitIds', entity),
    ...serializeSelectNumberData('divisionIds', entity),
    ...serializeSelectData('professionIds', entity),
    ...serializeSelectData('domainIds', entity),
    ...serializeRequestCustomerIds('customerIds', responsible, currentTab, entity),
    ...serializeRequestExecutorIds('executorIds', entity),
    ...serializeSelectNumberData('countryIds', entity),
    ...serializeSelectData('cityIds', entity),
    ...serializeSelectData('officeIds', entity),
    ...serializerBooleanData('forProject', entity),
    ...serializerBooleanData('forPreoffer', entity),
    ...serializeDateSegmentRange('deadlineFrom', 'deadlineTo', 'deadline', entity),
    ...serializerDateRangeData('createdFrom', 'createdTo', 'createdDate', entity, formatFilterDate),
  });

  const getSerializedRequestFilters = () => serialieFilters(filtersConfig);
  const getInitialRequestFilters = () => serialieFilters(preloadedState.request);

  const serializedRequestFilters = useMemo(getSerializedRequestFilters, [
    responsible,
    currentTab,
    filtersConfig.searchBarRequest.state.value,
    pagable,
    sort,
  ]);

  return {
    getSerializedRequestFilters,
    getInitialRequestFilters,
    serializedRequestFilters,
    filtersConfig,
    request: filtersConfig,
    responsible,
    currentTab,
    pagable,
    sort,
    ...filterMethods,
    ...rest,
  };
};

// Loads the values of an active list filter once per page visit; the keys still loading are locked in the filters bar.
// Values that failed to load are requested again when a filter is opened
export const useRequestFilterValues = (filters: FiltersState['request']) => {
  const dispatch = useAppDispatch();
  const requestedFilters = useRef(new Set<RequestListFilter>());
  const [loadingFilters, setLoadingFilters] = useState<RequestListFilter[]>([]);

  const activeFilters = RequestListFilters.filter((key) => filters[key]?.active);
  const openedFilter = RequestListFilters.find((key) => filters[key]?.isOpen);

  useEffect(() => {
    activeFilters.forEach((key) => {
      if (requestedFilters.current.has(key)) return;

      requestedFilters.current.add(key);
      setLoadingFilters((current) => [...current, key]);
      dispatch(getRequestFilterValuesThunk(key))
        .unwrap()
        .catch(() => requestedFilters.current.delete(key))
        .finally(() => setLoadingFilters((current) => current.filter((item) => item !== key)));
    });
  }, [activeFilters.join(), openedFilter, dispatch]);

  return { loadingFilters };
};

export const useVacancyFilters = () => {
  const { ...filterMethods } = useFilterMethods('vacancy');
  const { vacancy, responsible, currentTab, pagable, sort, ...rest } = useFiltersData();

  const serialieFilters = (entity: FiltersState['vacancy']) => ({
    ...lifecycleStatusSerializer('lifecycleStatus', currentTab),
    ...serializeSelectData('countries', entity),
    ...serializeSelectData('statusIds', entity),
    ...serializeSelectData('technologyIds', entity),
    ...serializeSelectData('domainIds', entity),
    ...serializerBooleanData('published', entity),
    ...serializeNestedSelectData('languageFilter', 'language', 'grades', entity),
    ...serializeNestedSelectData('professionFilter', 'professionId', 'grades', entity),
    ...candidateStringDataSerializer('searchBarRequest', 'searchBarRequest', entity),
    ...candidateResponsibleEmployeeDataSerializer('authorIds', responsible, currentTab),
    ...serializeRequestAuthorEmploye('employe', 'authorIds', entity),
    ...serializeSelectData('hiringPurpose', entity),
    ...serializeBooleanList('isBillable', entity),
  });

  const getSerializedVacancyFilters = () => serialieFilters(vacancy);
  const getInitialVacancyFilters = () => serialieFilters(preloadedState.vacancy);

  const serializedRequestFilters = useMemo(getSerializedVacancyFilters, [
    responsible,
    currentTab,
    vacancy.searchBarRequest.state.value,
    pagable,
    sort,
  ]);

  return {
    getSerializedVacancyFilters,
    getInitialVacancyFilters,
    serializedRequestFilters,
    filtersConfig: vacancy,
    vacancy,
    responsible,
    currentTab,
    pagable,
    sort,
    ...filterMethods,
    ...rest,
  };
};

export const usePreofferFilters = () => {
  const { ...filterMethods } = useFilterMethods('preoffer');
  const { preoffer, responsible, pagable, ...rest } = useFiltersData();

  const serializeFilters = (entity: FiltersState['preoffer']) => ({
    ...serializeBooleanList('attendingInterviews', entity),
    ...serializerDateRangeData('statusCreateDateFrom', 'statusCreateDateTo', 'statusCreateDate', entity, (date) =>
      format(new Date(date), DateFormats.YearFirst),
    ),
    ...serializeSelectData('position', entity),
    ...serializeSelectData('grade', entity),
    ...serializeSelectNumberData('unit', entity),
    ...serializeSelectNumberData('department', entity),
    ...serializeSelectNumberData('division', entity),
    ...serializeRequestAuthorEmploye('responsibleEmployee', 'responsibleEmployee', entity),
    ...serializeRequestAuthorEmploye('head', 'head', entity),
    ...serializeRequestAuthorEmploye('resourceManager', 'resourceManager', entity),
    ...serializeBooleanList('attachedCv', entity),
    ...serializeBooleanList('attachedNda', entity),
    ...candidateStringDataSerializer('searchBar', 'searchBar', entity),
  });

  const getSerializedPreofferFilters = () => serializeFilters(preoffer);
  const getInitialPreofferFilters = () => serializeFilters(preloadedState.preoffer);

  const serializedPreofferFilters = useMemo(getSerializedPreofferFilters, [
    responsible,
    preoffer.searchBar.state.value,
    pagable,
  ]);

  return {
    getSerializedPreofferFilters,
    getInitialPreofferFilters,
    serializedPreofferFilters,
    filtersConfig: preoffer,
    preoffer,
    responsible,
    pagable,
    ...filterMethods,
    ...rest,
  };
};
